import type { WordDocumentTemplateInput, WordDocumentTemplateSectionInput } from '~/api/graphql';

/** Form-state representation of a single section. Extends the GraphQL input
 *  type with an in-memory `existingFile` slot used by the editor to render
 *  the "currently stored file" row with a Replace flow. The extra field is
 *  stripped before the section is sent over the wire (see
 *  `sectionInputForSubmit`). */
export type WordTemplateSectionFormEntry = WordDocumentTemplateSectionInput & {
  existingFile?: { id: string; name: string; url?: string | null } | null;
};

/** Form-state representation of the full Word template input. Mirrors the
 *  GraphQL input type but lets each section carry the in-memory `existingFile`
 *  metadata that powers the Replace-file UX in edit mode. */
export type WordTemplateFormState = Omit<WordDocumentTemplateInput, 'sections'> & {
  sections: WordTemplateSectionFormEntry[];
};

/** Title applied at API-submission time when a section's title is empty —
 *  the only path that reaches the API with an empty title today is the
 *  implicit single section produced by "simple" mode in the create dialog. */
export const SIMPLE_SECTION_SUBMIT_TITLE = 'Document';

/** Strip the in-memory `existingFile` field so what we send to the server
 *  matches the GraphQL `WordDocumentTemplateSectionInput` shape exactly. */
const sectionInputForSubmit = (
  entry: WordTemplateSectionFormEntry
): WordDocumentTemplateSectionInput => {
  const { existingFile: _existingFile, ...rest } = entry;
  return rest;
};

/** Build the in-state representation of the single section used by "simple"
 *  mode in the create dialog: blank title (so a toggle to sectioned reveals an
 *  empty input), flags off (except `required`, which defaults on so the
 *  section is always included in generated documents), and an optional file. */
export const createDefaultSingleSection = (
  uploadTemplate: File | null = null
): WordTemplateSectionFormEntry => ({
  title: '',
  setupGuidance: null,
  static: false,
  repeatable: false,
  required: true,
  uploadTemplate,
  templateFileId: null
});

/** Build the in-state representation of a fresh empty section: empty title,
 *  no file, `required` on by default (toggle off to mark optional). */
export const createEmptySection = (): WordTemplateSectionFormEntry => ({
  title: '',
  setupGuidance: null,
  static: false,
  repeatable: false,
  required: true,
  uploadTemplate: null,
  templateFileId: null
});

/** Right before sending sections to the API, replace any blank titles with
 *  the simple-mode placeholder (`Document`). Used by the create dialog only —
 *  the editor always sends user-entered titles through as-is so the server
 *  surfaces the per-section "title required" error in the right card. */
export const sectionsWithSimpleModeTitleFallback = (
  sections: WordTemplateSectionFormEntry[]
): WordTemplateSectionFormEntry[] =>
  sections.map((s) => (s.title.trim() ? s : { ...s, title: SIMPLE_SECTION_SUBMIT_TITLE }));

/** Append a sections payload to an existing `FormData`. Serializes the
 *  sections (minus the in-memory `existingFile`) as JSON under the
 *  `sections` key, and attaches any `uploadTemplate` File objects under
 *  `sections[<i>].uploadTemplate` so the route action can rebuild a typed
 *  array on the other side without losing file references. */
export const appendSectionsToFormData = (
  formData: FormData,
  sections: WordTemplateSectionFormEntry[]
) => {
  formData.set(
    'sections',
    JSON.stringify(
      sections.map(sectionInputForSubmit).map((s) => ({
        id: s.id ?? null,
        title: s.title,
        setupGuidance: s.setupGuidance ?? null,
        static: s.static,
        repeatable: s.repeatable,
        required: s.required,
        templateFileId: s.templateFileId ?? null
      }))
    )
  );
  sections.forEach((s, i) => {
    if (s.uploadTemplate) {
      formData.set(`sections[${i}].uploadTemplate`, s.uploadTemplate);
    }
  });
};
